import React, { createContext, FC, useContext, useEffect, useState } from 'react';
import { ObraProperties, UnidadObra, WandaProject } from '../../../types/ObraDetails';
import styles from './technicalAspects.module.scss';
import WorkTypesMagnitudes from './workTypesMagnitudes/workTypesMagnitudes';
import BuildingProcesses from './buildingProcesses/buildingProcess';
import WorkUnits from './workUnits/workUnits';
import NoDataPicture from '../components/noDataPicture/noDataPicture';
import coffeeBreak from '../../../assets/coffeeBreak.svg';
import { useTranslation } from 'react-i18next';
import ObrasAPI from '../../../services/APIObras';
import ErrorDetails from '../components/ErrorDetails/ErrorDetails';
import { DetailsViewContext } from '../../../context/DetailsViewProvider';
import { SaveContext } from '../../../context/SaveProvider';
import { TechnicalAspectsLoadingSkeleton } from './components/technicalAspectsLoadingSkeleton/technicalAspectsLoadingSkeleton';

interface TechnicalAspectsProps {
  obra: ObraProperties;
  codCentro: string;
  wandaProject?: WandaProject;
  wandaProjectLoading: boolean;
  wandaProjectError: boolean;
  userMode: string;
  version: 'viewer' | 'editor';
  errors?: Array<{
    field?: string;
    fieldField?: string;
    index?: string | number;
  }>;
}
interface TechnicalAspectsContextValues {
  buildingProcessUnits: BuildingProcessUnits[];
  setBuildingProcessUnits: React.Dispatch<React.SetStateAction<BuildingProcessUnits[]>>
}
interface BuildingProcessUnits {
  index: number;
  units: UnidadObra[];
}
const defaultTechnicalAspectsContextValues: TechnicalAspectsContextValues = {
  buildingProcessUnits: [],
  setBuildingProcessUnits: () => undefined,
};
export const TechnicalAspectsContext = createContext<TechnicalAspectsContextValues>(defaultTechnicalAspectsContextValues);

const TechnicalAspects: FC<TechnicalAspectsProps> = (props) => {
  const {
    obra,
    codCentro,
    wandaProject,
    wandaProjectLoading,
    wandaProjectError,
    userMode,
    version,
    errors,
  } = props;
  const [
    t,
    i18n,
  ] = useTranslation();
  const {
    setObra,
    loadedDetailSections,
    markDetailSectionLoaded,
  } = useContext(DetailsViewContext);
  const saveContext = useContext(SaveContext);
  const [technicalAspectsLoaded, setTechnicalAspectsLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const sectionKey = `${codCentro}:${version}:${i18n.language}:technicalAspects`;

  useEffect(() => {
    let active = true;
    setTechnicalAspectsLoaded(
      false,
    );
    setLoadError(false);
    if (!wandaProject) {
      return () => {
        active = false;
      };
    }

    if (loadedDetailSections[sectionKey]) {
      setTechnicalAspectsLoaded(true);
      return () => {
        active = false;
      };
    }

    ObrasAPI.getInstance().getObraTechnicalAspectsAPI(codCentro, wandaProject, version)
      .then((response) => {
        if (!active) return;
        setObra({
          ...obra,
          aspectosTecnicos: response.aspectosTecnicos,
        });
        markDetailSectionLoaded(sectionKey);
        setTechnicalAspectsLoaded(true);
      })
      .catch(() => {
        if (active) setLoadError(true);
      });

    return () => {
      active = false;
    };
  }, [codCentro, wandaProject, version, i18n.language, saveContext.reloadData, loadedDetailSections, markDetailSectionLoaded, sectionKey]);

  const technicalAspectsNotEmpty = obra.aspectosTecnicos?.tiposObras?.length > 0 ||
    obra.aspectosTecnicos?.procesosConstructivos?.length > 0 ||
    obra.aspectosTecnicos?.unidades?.length > 0;

  const [buildingProcessUnits, setBuildingProcessUnits] =
    useState(obra.aspectosTecnicos?.procesosConstructivos.map((pc, i) => {
      return {
        index: i,
        units: pc.unidades?.map(unit => unit),
      };
    }) || []);

  useEffect(() => {
    setBuildingProcessUnits(obra.aspectosTecnicos?.procesosConstructivos.map((pc, i) => {
      return {
        index: i,
        units: pc.unidades?.map(unit => unit),
      };
    }) || []);
  }, [obra.aspectosTecnicos]);

  if (loadError || wandaProjectError) return <ErrorDetails />;
  if (!technicalAspectsLoaded || wandaProjectLoading) return <TechnicalAspectsLoadingSkeleton />;

  return (
    <TechnicalAspectsContext.Provider value={{
      buildingProcessUnits,
      setBuildingProcessUnits,
    }}>
      <div data-testid='technical-aspects-test' className={styles.technicalAspectsContainer}>
        {
          technicalAspectsNotEmpty
            ? <>
              <div className={styles.section}>
                <WorkTypesMagnitudes obra={obra} errors={errors?.filter(e => e.field === 'tiposObras')} />
              </div>
              <div className={styles.section}>
                <BuildingProcesses obra={obra} userMode={userMode} errors={errors?.filter(e => e.field === 'procesosConstructivos')} />
              </div>
              <div className={styles.section}>
                <WorkUnits obra={obra} errors={errors ? [
                  ...errors.filter(e => e.field === 'unidades'),
                  ...errors.filter(e => e.field === 'procesosConstructivos' && e.fieldField === 'medicion')
                ] : []} />
              </div>
            </>
            : <NoDataPicture
              id='noWorkPicture'
              picture={coffeeBreak}
              text={t('details.technical.noMainWork')}
              tip={t('details.technical.noMainWorkTip') as string}
            />
        }
      </div>
      <div className='footer' style={{ marginBottom: '75px' }}></div>
    </TechnicalAspectsContext.Provider>
  );
};

export default TechnicalAspects;
